import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, RefreshCw, AlertCircle, CreditCard, Star, XCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import adminService, { AdminPaymentMethod, CreatePaymentMethodRequest, UpdatePaymentMethodRequest } from '../../services/admin/adminService';
import DataTable from '../../components/common/DataTable';

const PaymentMethods: React.FC = () => {
    const [methods, setMethods] = useState<AdminPaymentMethod[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [modal, setModal] = useState<{ show: boolean; mode: 'add' | 'edit'; data: Partial<CreatePaymentMethodRequest> & { payment_method_id?: number } }>({
        show: false,
        mode: 'add',
        data: {}
    });
    const [deleteModal, setDeleteModal] = useState<{ show: boolean; method: AdminPaymentMethod | null }>({
        show: false,
        method: null
    });
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [modalError, setModalError] = useState<string | null>(null);

    useEffect(() => {
        fetchMethods();
    }, []);

    const fetchMethods = async (showToast = false) => {
        try {
            if (showToast) {
                setRefreshing(true);
            } else {
                setLoading(true);
            }

            const data = await adminService.getAllPaymentMethods();
            setMethods(data);

            if (showToast) {
                toast.success('Payment methods refreshed!');
            }
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to fetch payment methods');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleSaveMethod = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!modal.data) return;

        try {
            setSaving(true);

            // Basic validation
            if (!modal.data.payment_method_name) {
                toast.error("Payment method identifier is required");
                setSaving(false);
                return;
            }

            // Prepare data
            const payload: any = { ...modal.data };

            // Handle optional fields for backend validation
            // If the field is empty or just whitespace, we remove it from the payload to avoid strict validation errors
            // like "" not allowed, null not allowed, or " " not being a valid email format.
            ['description', 'email', 'phone', 'username'].forEach(key => {
                const value = payload[key];
                if (value === undefined || value === null || (typeof value === 'string' && value.trim() === "")) {
                    delete payload[key];
                }
            });

            // Convert comma-separated strings to arrays if they are strings (kept for compatibility in case backend expects them, though not shown in simplified UI)
            if (typeof payload.supported_currencies === 'string') {
                payload.supported_currencies = payload.supported_currencies.split(',').map((s: string) => s.trim()).filter(Boolean);
            }
            if (typeof payload.allowed_countries === 'string') {
                payload.allowed_countries = payload.allowed_countries.split(',').map((s: string) => s.trim()).filter(Boolean);
            }

            if (modal.mode === 'add') {
                await adminService.createPaymentMethod(payload as CreatePaymentMethodRequest);
                toast.success('Payment method created successfully!');
            } else {
                if (!modal.data.payment_method_id) {
                    toast.error("Missing payment method ID for update");
                    return;
                }

                // Extract payment_method_id for the URL param, use rest for body
                const { payment_method_id, ...updateData } = payload;

                await adminService.updatePaymentMethod({
                    method_id: payment_method_id,
                    ...updateData
                } as UpdatePaymentMethodRequest);
                toast.success('Payment method updated successfully!');
            }
            setModal({ show: false, mode: 'add', data: {} });
            setModalError(null);
            fetchMethods();
        } catch (error: any) {
            console.error('Save error details:', error.response?.data || error);

            let message = 'Failed to save payment method';

            if (error.response?.data) {
                const data = error.response.data;
                // Try to get message from standard formats
                message = data.message || data.error || (typeof data === 'string' ? data : message);

                // If there's an errors array (common in Joi/Express validation)
                if (data.errors) {
                    if (Array.isArray(data.errors)) {
                        message = data.errors.map((err: any) => err.message || err).join('; ');
                    } else if (typeof data.errors === 'object') {
                        message = Object.values(data.errors).join('; ');
                    }
                }
            } else if (error.message) {
                message = error.message;
            }

            setModalError(message);
            toast.error(message, {
                id: 'save-error', // Prevent multiple toasts for same action
                duration: 5000
            });
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteMethod = async () => {
        if (!deleteModal.method) return;

        try {
            setDeleting(true);
            await adminService.deletePaymentMethod(deleteModal.method.payment_method_id);
            toast.success('Payment method deleted successfully!');
            setDeleteModal({ show: false, method: null });
            fetchMethods();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to delete payment method');
        } finally {
            setDeleting(false);
        }
    };

    const toggleStatus = async (method: AdminPaymentMethod) => {
        try {
            const newStatus = method.status === 'active' ? 'inactive' : 'active';
            await adminService.togglePaymentMethodStatus(method.payment_method_id, newStatus);
            toast.success(`Method ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
            fetchMethods();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to update status');
        }
    };

    const setDefault = async (method: AdminPaymentMethod) => {
        if (method.is_default) return;
        try {
            await adminService.setDefaultPaymentMethod(method.payment_method_id);
            toast.success('Default payment method updated');
            fetchMethods();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to set default method');
        }
    };

    const openAddModal = () => {
        setModalError(null);
        setModal({
            show: true,
            mode: 'add',
            data: {
                status: 'active', // Default to active as per image
                is_default: false,
                enable_for_users: true,
                sort_order: 0,
                supported_currencies: [],
                allowed_countries: [],
                payment_method_name: '',
                display_name: '',
                username: '',
                email: '',
                phone: '',
                description: ''
            }
        });
    };

    const openEditModal = (method: AdminPaymentMethod) => {
        setModalError(null);
        setModal({
            show: true,
            mode: 'edit',
            data: {
                payment_method_id: method.payment_method_id,
                payment_method_name: method.payment_method_name,
                display_name: method.display_name || undefined,
                description: method.description || undefined,
                username: method.username || undefined,
                email: method.email || undefined,
                phone: method.phone || undefined,
                status: method.status,
                is_default: Boolean(method.is_default),
                sort_order: method.sort_order,
                enable_for_users: Boolean(method.enable_for_users),
                // Keep these for data integrity even if not edited
                account_name: method.account_name || undefined,
                account_number: method.account_number || undefined,
                routing_number: method.routing_number || undefined,
                instructions: method.instructions || undefined,
                supported_currencies: method.supported_currencies || [],
                allowed_countries: method.allowed_countries || [],
                minimum_amount: method.minimum_amount != null ? Number(method.minimum_amount) : undefined,
                maximum_amount: method.maximum_amount != null ? Number(method.maximum_amount) : undefined,
            }
        });
    };

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold text-secondary-900">Payment Methods</h1>
                    <p className="text-secondary-600 mt-1">Configure and manage payment options for the platform</p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={() => fetchMethods(true)}
                        disabled={refreshing}
                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                    >
                        <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
                        Refresh
                    </button>
                    <button
                        onClick={openAddModal}
                        className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2"
                    >
                        <Plus size={18} />
                        Add Payment Method
                    </button>
                </div>
            </div>

            {/* Methods Table */}
            <DataTable
                data={methods}
                loading={loading}
                searchable={true}
                searchPlaceholder="Search payment methods..."
                searchFields={['payment_method_name', 'display_name', 'description']}
                defaultPageSize={10}
                emptyMessage="No payment methods found"
                emptyIcon={<CreditCard size={64} className="text-secondary-300 mx-auto mb-4" />}
                columns={[
                    {
                        header: 'PAYMENT METHOD',
                        accessor: (row) => (
                            <div>
                                <p className="font-bold text-secondary-900">{row.display_name || row.payment_method_name}</p>
                                <p className="text-sm text-secondary-500">{row.payment_method_name}</p>
                            </div>
                        ),
                        sortable: true
                    },
                    {
                        header: 'USERNAME/ACCOUNT',
                        accessor: (row) => (
                            <span className="text-secondary-900 font-medium">
                                {row.username || row.account_number || '-'}
                            </span>
                        )
                    },
                    {
                        header: 'STATUS',
                        accessor: (row) => (
                            <button
                                onClick={(e) => { e.stopPropagation(); toggleStatus(row); }}
                                className={`px-3 py-1 rounded-full text-xs font-semibold border ${row.status === 'active'
                                    ? 'bg-green-100 text-green-800 border-green-200'
                                    : 'bg-secondary-100 text-secondary-600 border-secondary-200'
                                    }`}
                            >
                                {row.status === 'active' ? 'active' : 'inactive'}
                            </button>
                        ),
                        align: 'center',
                        sortable: true
                    },
                    {
                        header: 'DEFAULT',
                        accessor: (row) => (
                            <button
                                onClick={(e) => { e.stopPropagation(); setDefault(row); }}
                                className={`p-1 rounded-full transition-colors ${row.is_default
                                    ? 'text-yellow-400'
                                    : 'text-gray-300 hover:text-gray-400'
                                    }`}
                                title={row.is_default ? 'Default Method' : 'Set as Default'}
                            >
                                {row.is_default ? (
                                    <Star size={20} fill="currentColor" strokeWidth={0} />
                                ) : (
                                    <XCircle size={20} />
                                )}
                            </button>
                        ),
                        align: 'center',
                        sortable: true
                    },
                    {
                        header: 'CREATED',
                        accessor: (row) => (
                            <span className="text-secondary-600">
                                {new Date(row.created_at).toLocaleDateString()}
                            </span>
                        ),
                        sortable: true
                    }
                ]}
                renderActions={(row) => (
                    <div className="flex items-center justify-center gap-2">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                openEditModal(row);
                            }}
                            className="bg-primary-50 hover:bg-primary-100 text-primary-600 p-2 rounded-lg transition-colors"
                            title="Edit"
                        >
                            <Edit2 size={18} />
                        </button>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setDeleteModal({ show: true, method: row });
                            }}
                            className="bg-red-50 hover:bg-red-100 text-red-600 p-2 rounded-lg transition-colors"
                            title="Delete"
                        >
                            <Trash2 size={18} />
                        </button>
                    </div>
                )}
            />

            {/* Add/Edit Modal - Simplified based on User Request */}
            {modal.show && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-white sticky top-0 z-10">
                            <h3 className="text-xl font-bold text-secondary-900">
                                {modal.mode === 'add' ? 'Create Payment Method' : 'Edit Payment Method'}
                            </h3>
                            <button
                                onClick={() => {
                                    setModal({ show: false, mode: 'add', data: {} });
                                    setModalError(null);
                                }}
                                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                            >
                                &times;
                            </button>
                        </div>

                        <form onSubmit={handleSaveMethod} className="p-6 space-y-6">
                            {modalError && (
                                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-3 animate-in fade-in duration-300">
                                    <AlertCircle size={20} className="mt-0.5 flex-shrink-0" />
                                    <p className="text-sm font-medium">{modalError}</p>
                                </div>
                            )}

                            {/* Row 1: Names */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="text-sm font-semibold text-secondary-800 block mb-2">Payment Method Name *</label>
                                    <input
                                        required
                                        type="text"
                                        placeholder="e.g., Zelle1"
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all placeholder-gray-400"
                                        value={modal.data.payment_method_name || ''}
                                        onChange={(e) => setModal({ ...modal, data: { ...modal.data, payment_method_name: e.target.value } })}
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-secondary-800 block mb-2">Display Name *</label>
                                    <input
                                        required
                                        type="text"
                                        placeholder="e.g., Bank Of Zelle"
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all placeholder-gray-400"
                                        value={modal.data.display_name || ''}
                                        onChange={(e) => setModal({ ...modal, data: { ...modal.data, display_name: e.target.value } })}
                                    />
                                </div>
                            </div>

                            {/* Row 2: Username & Email */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="text-sm font-semibold text-secondary-800 block mb-2">Username <span className="text-gray-400 font-normal">(Optional)</span></label>
                                    <input
                                        type="text"
                                        placeholder="e.g., americalzellebank"
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all placeholder-gray-400"
                                        value={modal.data.username || ''}
                                        onChange={(e) => setModal({ ...modal, data: { ...modal.data, username: e.target.value } })}
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-secondary-800 block mb-2">Email <span className="text-gray-400 font-normal">(Optional)</span></label>
                                    <input
                                        type="email"
                                        placeholder="e.g., payment@example.com"
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all placeholder-gray-400"
                                        value={modal.data.email || ''}
                                        onChange={(e) => setModal({ ...modal, data: { ...modal.data, email: e.target.value } })}
                                    />
                                </div>
                            </div>

                            {/* Row 3: Phone & Status */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="text-sm font-semibold text-secondary-800 block mb-2">Phone <span className="text-gray-400 font-normal">(Optional)</span></label>
                                    <input
                                        type="tel"
                                        placeholder="e.g., +1234567890"
                                        pattern="\+?[0-9]*"
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all placeholder-gray-400"
                                        value={modal.data.phone || ''}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (val === '' || /^\+?[0-9]*$/.test(val)) {
                                                setModal({ ...modal, data: { ...modal.data, phone: val } });
                                            }
                                        }}
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-secondary-800 block mb-2">Status</label>
                                    <select
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all text-secondary-900 bg-white"
                                        value={modal.data.status}
                                        onChange={(e) => setModal({ ...modal, data: { ...modal.data, status: e.target.value as 'active' | 'inactive' } })}
                                    >
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                </div>
                            </div>

                            {/* Row 4: Description */}
                            <div>
                                <label className="text-sm font-semibold text-secondary-800 block mb-2">Description <span className="text-gray-400 font-normal">(Optional)</span></label>
                                <textarea
                                    placeholder="Payment method description..."
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all placeholder-gray-400 resize-none"
                                    rows={4}
                                    value={modal.data.description || ''}
                                    onChange={(e) => setModal({ ...modal, data: { ...modal.data, description: e.target.value } })}
                                />
                            </div>

                            {/* Checkboxes */}
                            <div className="flex items-center gap-6 pt-2">
                                <label className="flex items-center gap-2 cursor-pointer select-none">
                                    <input
                                        type="checkbox"
                                        className="w-4 h-4 rounded text-primary-600 focus:ring-primary-500 border-gray-300"
                                        checked={!!modal.data.is_default}
                                        onChange={(e) => setModal({ ...modal, data: { ...modal.data, is_default: e.target.checked } })}
                                    />
                                    <span className="text-sm text-secondary-700">Set as default</span>
                                </label>
                            </div>

                            {/* Buttons */}
                            <div className="flex gap-4 pt-6 mt-6">
                                <button
                                    type="button"
                                    onClick={() => setModal({ show: false, mode: 'add', data: {} })}
                                    disabled={saving}
                                    className="flex-1 px-4 py-3 bg-white border border-gray-300 text-secondary-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="flex-1 px-4 py-3 bg-secondary-900 text-white rounded-lg hover:bg-secondary-800 transition-colors disabled:opacity-50 font-medium flex items-center justify-center gap-2"
                                >
                                    {saving ? 'Saving...' : (
                                        <>
                                            <CreditCard size={18} />
                                            {modal.mode === 'add' ? 'Create Payment Method' : 'Save Changes'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteModal.show && deleteModal.method && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full shadow-2xl">
                        <div className="p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-3 bg-red-100 rounded-full">
                                    <AlertCircle size={24} className="text-red-600" />
                                </div>
                                <h3 className="text-xl font-semibold text-secondary-900">Delete Payment Method</h3>
                            </div>

                            <p className="text-secondary-600 mb-6">
                                Are you sure you want to delete <strong>{deleteModal.method.display_name || deleteModal.method.payment_method_name}</strong>?
                                This action cannot be undone.
                            </p>

                            <div className="flex gap-3">
                                <button
                                    onClick={handleDeleteMethod}
                                    disabled={deleting}
                                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 font-bold"
                                >
                                    {deleting ? 'Deleting...' : 'Delete'}
                                </button>
                                <button
                                    onClick={() => setDeleteModal({ show: false, method: null })}
                                    disabled={deleting}
                                    className="flex-1 px-4 py-2 bg-gray-200 text-secondary-900 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaymentMethods;
